import { useEffect, useMemo, useRef, useState } from "react";
import {
  AimOutlined,
  AndroidOutlined,
  AppleOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  CopyOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  FileDoneOutlined,
  IdcardOutlined,
  LoginOutlined,
  SearchOutlined,
  SendOutlined,
  ShareAltOutlined,
  SafetyCertificateOutlined,
  SolutionOutlined,
  ThunderboltOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { AutoComplete, Button, Drawer, Input, Modal, Pagination, Skeleton, Space, Tag, Typography, message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import JobDetailsDrawer from "components/jobs/JobDetailsDrawer";
import OliveebProductAds from "components/promotions/OliveebProductAds";
import SignInRequiredModal from "components/auth/SignInRequiredModal";
import { useAuth } from "contexts/AuthContext";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import { PRODUCT_LINKS } from "constants";
import { bearerGuestToken, ensureGuestSession } from "utils/guestSession";
import { ensureGoogleMapsPlacesLoaded } from "utils/googleMapsLoader";
import { OLIVEEB_COLORS } from "workspaces/colors";
import { hasRichText, richTextMarkup } from "./richTextUtils";

const { Text } = Typography;

const APP_SHARE_LINK = `${PRODUCT_LINKS.olreadia || ""}/download`;
const DOWNLOAD_PAGE_LINK = "/download";
const IOS_DOWNLOAD_LINK = "/download?platform=ios";
const ANDROID_DOWNLOAD_LINK = "/download?platform=android";
const APP_SHARE_MESSAGE =
  "Join me on OCarely - manage residents, care processes, schedules, daily notes, documents, billing, websites, and AI-assisted care workflows. " +
  APP_SHARE_LINK;
const RESULTS_PAGE_SIZE = 6;
const uniqueSuggestionValues = (values) => {
  const seen = new Set();
  return values.reduce((items, value) => {
    const displayValue = String(value || "").trim().replace(/\s+/g, " ");
    const key = displayValue.toLowerCase();
    if (!displayValue || seen.has(key)) return items;
    seen.add(key);
    items.push(displayValue);
    return items;
  }, []);
};

const popularSearchLabel = (item) => {
  if (typeof item === "string") return item.trim();
  return String(item?.label || item?.searchQuery || item?.query || item?.name || "").trim();
};

const titleCase = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const popularLocationLabel = (item) => {
  if (typeof item === "string") return item.trim();

  const direct = String(item?.label || item?.location || item?.name || "").trim();
  if (direct) return direct;

  const city = titleCase(item?.city);
  const county = titleCase(item?.county);
  const state = String(item?.state || "").trim().toUpperCase();
  const country = String(item?.country || "").trim().toUpperCase();

  if (city && state) return `${city}, ${state}`;
  if (city && country) return `${city}, ${country}`;
  if (county && state) return `${county} County, ${state}`;
  if (state && country) return `${state}, ${country}`;
  return city || county || state || country;
};

const pageContent = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.jobOpportunities)) return data.jobOpportunities;
  if (Array.isArray(data?.opportunities)) return data.opportunities;
  return [];
};

const pageTotalElements = (data) => {
  if (typeof data?.totalElements === "number") return data.totalElements;
  if (typeof data?.total === "number") return data.total;
  if (typeof data?.totalCount === "number") return data.totalCount;
  return pageContent(data).length;
};

const renderedPageTotal = (data, items, page) => {
  const apiTotal = pageTotalElements(data);
  const pageOffset = Math.max(page - 1, 0) * RESULTS_PAGE_SIZE;
  const renderedCount = items.length;

  if (renderedCount < RESULTS_PAGE_SIZE) {
    return pageOffset + renderedCount;
  }

  return Math.max(apiTotal, pageOffset + renderedCount);
};

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const namedRefLabel = (value) => {
  if (!value) return "";
  if (typeof value === "string") return titleCase(value);
  return firstText(value.name, value.label, value.title, value.code);
};

const toList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (value instanceof Set) return Array.from(value);
  return [value];
};

const normalizeStrings = (value) =>
  toList(value)
    .map((item) => (item && typeof item === "object" ? namedRefLabel(item) : titleCase(item)))
    .filter(Boolean);

const compactList = (...groups) => groups.flat().filter(Boolean);

const firstValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return null;
};

const formatDate = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const formatMoney = (value, currency = "USD") => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value || "");
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: Number.isInteger(numeric) ? 0 : 2,
    }).format(numeric);
  } catch {
    return `$${numeric.toLocaleString()}`;
  }
};

const salaryLabel = (job) => {
  const direct = firstValue(job?.salaryExpectation, job?.salaryExpectations, job?.salaryRange, job?.compensation, job?.pay, job?.payRange);
  if (direct) return String(direct);
  const currency = job?.currency || job?.salaryCurrency || job?.payCurrency || "USD";
  const min = firstValue(job?.salaryMin, job?.minSalary, job?.minimumSalary, job?.payMin, job?.minPay);
  const max = firstValue(job?.salaryMax, job?.maxSalary, job?.maximumSalary, job?.payMax, job?.maxPay);
  const single = firstValue(job?.salary, job?.payRate, job?.hourlyRate);
  const cadence = titleCase(firstValue(job?.salaryPeriod, job?.payPeriod, job?.rateUnit, job?.payFrequency));
  const suffix = cadence ? ` / ${cadence.toLowerCase()}` : "";
  if (min && max) return `${formatMoney(min, currency)} - ${formatMoney(max, currency)}${suffix}`;
  if (min) return `From ${formatMoney(min, currency)}${suffix}`;
  if (max) return `Up to ${formatMoney(max, currency)}${suffix}`;
  if (single) return `${formatMoney(single, currency)}${suffix}`;
  return "Not set";
};

const statusColor = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "OPEN" || normalized === "ACTIVE") return "green";
  if (normalized === "DRAFT") return "gold";
  if (normalized === "CLOSED" || normalized === "ARCHIVED") return "red";
  return "blue";
};

const roleOptionsFor = (roles, query) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return [];

  const seenRoleTitles = new Set();

  return roles
    .map((role, index) => {
      const title = String(role.title || "").toLowerCase();
      const synonyms = Array.isArray(role.synonyms)
        ? role.synonyms.map((value) => String(value || "").toLowerCase())
        : [];
      const description = String(role.description || "").toLowerCase();
      let rank = Number.POSITIVE_INFINITY;

      if (title === normalizedQuery) rank = 0;
      else if (title.startsWith(normalizedQuery)) rank = 1;
      else if (title.includes(normalizedQuery)) rank = 2;
      else if (synonyms.some((value) => value === normalizedQuery)) rank = 3;
      else if (synonyms.some((value) => value.startsWith(normalizedQuery))) rank = 4;
      else if (synonyms.some((value) => value.includes(normalizedQuery))) rank = 5;
      else if (description.includes(normalizedQuery)) rank = 6;

      return { role, index, rank };
    })
    .filter((item) => Number.isFinite(item.rank))
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        String(a.role.title || "").localeCompare(String(b.role.title || "")) ||
        a.index - b.index,
    )
    .filter(({ role }) => {
      const title = String(role.title || "").trim().replace(/\s+/g, " ");
      const key = title.toLowerCase();
      if (!title || seenRoleTitles.has(key)) return false;
      seenRoleTitles.add(key);
      return true;
    })
    .slice(0, 8)
    .map(({ role }) => ({
      value: role.title,
      label: (
        <Space>
          <SolutionOutlined style={{ color: OLIVEEB_COLORS.oliveebGreen }} />
          <span>{role.title}</span>
        </Space>
      ),
    }));
};

const locationLabel = (location) => {
  if (!location) return "";
  if (typeof location === "string") return location;
  return firstText(
    location.label,
    location.name,
    location.address,
    location.formattedAddress,
    [location.city, location.state, location.country].filter(Boolean).join(", "),
  );
};

const jobCardFromApi = (job) => {
  const specializations = normalizeStrings(job?.specializations || job?.requiredSpecializations);
  const skills = uniqueSuggestionValues([
    ...normalizeStrings(job?.skills || job?.requiredSkills),
    ...normalizeStrings(job?.otherRequiredSkills),
    ...normalizeStrings(job?.requiredWorkAreas),
    ...normalizeStrings(job?.otherRequiredWorkAreas),
  ]);
  const locations = toList(job?.requiredLocations).map(locationLabel).filter(Boolean);
  const postedSource = job?.createdAt || job?.postedAt || job?.postStartDate;
  return {
    id: job?.id || job?.jobOpportunityId,
    title: job?.title || "Untitled job post",
    organization: namedRefLabel(job?.workspace) || namedRefLabel(job?.poster) || titleCase(job?.posterType) || "Employer post",
    location: locations[0] || titleCase(job?.locationType) || "",
    type: normalizeStrings(job?.workSchedules)[0] || titleCase(job?.workSchedule) || titleCase(job?.status || "Open"),
    badge: titleCase(job?.status || "Open"),
    postedDate: postedSource ? formatDate(postedSource) : "",
    specialization: specializations[0] || normalizeStrings(job?.requiredWorkAreas)[0] || "",
    summary: firstText(job?.summary, job?.description, job?.responsibilities, job?.requirements),
    skills: uniqueSuggestionValues([...specializations, ...skills]),
  };
};

const talentCardFromApi = (profile) => {
  const profileId = profile?.professionProfileId || profile?.profileId || profile?.id;
  const skills = uniqueSuggestionValues([
    ...normalizeStrings(profile?.skills || profile?.interestedServices),
    ...normalizeStrings(profile?.specializations || profile?.sectors),
  ]);
  const locations = normalizeStrings(profile?.locations || profile?.interestedLocations);
  return {
    id: profileId,
    title: firstText(profile?.displayName, profile?.name, profile?.fullName, profile?.profileName, profile?.title) || "Talent profile",
    organization: firstText(profile?.professionTitle, profile?.headline, profile?.profileTitle) || "Professional profile",
    location: locations[0] || firstText(profile?.location, profile?.city, profile?.state),
    type: profile?.availableNow ? "Available now" : titleCase(profile?.educationLevel) || "Open to opportunities",
    badge: profile?.isVerified ? "Identity verified" : profile?.isBackgroundChecked ? "Background checked" : "Profile",
    summary: firstText(profile?.profileSummary, profile?.summary, profile?.description, profile?.experience),
    skills,
  };
};

const LandingTalentDetailsDrawer = ({ profile, loading, open, onContact, onClose }) => {
  const skills = uniqueSuggestionValues([
    ...normalizeStrings(profile?.skills || profile?.interestedServices),
    ...normalizeStrings(profile?.specializations || profile?.sectors),
    ...normalizeStrings(profile?.services || profile?.serviceOfferings),
  ]);
  const locations = normalizeStrings(profile?.locations || profile?.interestedLocations);
  const displayName = firstText(
    profile?.displayName,
    profile?.name,
    profile?.fullName,
    profile?.profileName,
    profile?.title,
  );
  const headline = firstText(profile?.professionTitle, profile?.headline, profile?.profileTitle);
  const summary = firstText(
    profile?.profileSummary,
    profile?.summary,
    profile?.description,
    profile?.experience,
  );
  const detailItems = [
    {
      label: "Location",
      value: locations[0] || firstText(profile?.location, profile?.city, profile?.state) || "Not set",
      icon: <EnvironmentOutlined />,
    },
    {
      label: "Availability",
      value: profile?.availableNow ? "Available now" : "Open to opportunities",
      icon: <ClockCircleOutlined />,
    },
    {
      label: "Verification",
      value: profile?.isVerified
        ? "Identity verified"
        : profile?.isBackgroundChecked
          ? "Background checked"
          : "Profile",
      icon: <SafetyCertificateOutlined />,
    },
    {
      label: "Education",
      value: titleCase(profile?.educationLevel) || "Not set",
      icon: <IdcardOutlined />,
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width="min(440px, 100vw)"
      title={displayName || "Talent details"}
      className="olreadia-job-drawer"
      extra={
        profile ? (
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={onContact}
            style={{
              background: OLIVEEB_COLORS.oliveebGreen,
              borderColor: OLIVEEB_COLORS.oliveebGreen,
              fontWeight: 900,
            }}
          >
            Contact
          </Button>
        ) : null
      }
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : !profile ? (
        <div className="olreadia-job-drawer-empty">
          <Text className="olreadia-empty-title">Talent details are not available.</Text>
        </div>
      ) : (
        <Space direction="vertical" size={9} className="olreadia-job-drawer-content">
          <div className="olreadia-job-drawer-hero">
            <Space wrap>
              <Tag color={profile?.isVerified ? "green" : "blue"}>
                {profile?.isVerified ? "Verified" : "Talent"}
              </Tag>
              {profile?.availableNow ? <Tag color="green">Available now</Tag> : null}
            </Space>
            <h2>{displayName || "Talent profile"}</h2>
            {headline ? <Text>{headline}</Text> : null}
          </div>

          <div className="olreadia-job-drawer-grid">
            {detailItems.map((item) => (
              <div key={item.label} className="olreadia-job-drawer-fact">
                <span>{item.icon}</span>
                <div>
                  <Text type="secondary">{item.label}</Text>
                  <strong>{item.value}</strong>
                </div>
              </div>
            ))}
          </div>

          {skills.length ? (
            <div className="olreadia-job-drawer-block">
              <Text type="secondary">Skills and services</Text>
              <Space wrap size={[4, 4]}>
                {skills.map((item) => <Tag key={item}>{item}</Tag>)}
              </Space>
            </div>
          ) : null}

          {locations.length ? (
            <div className="olreadia-job-drawer-block">
              <Text type="secondary">Preferred locations</Text>
              <Space wrap size={[4, 4]}>
                {locations.map((item) => <Tag key={item}>{item}</Tag>)}
              </Space>
            </div>
          ) : null}

          {hasRichText(summary) ? (
            <div className="olreadia-job-drawer-section">
              <Text type="secondary">Summary</Text>
              <div className="olreadia-rich-text" dangerouslySetInnerHTML={richTextMarkup(summary)} />
            </div>
          ) : null}
        </Space>
      )}
    </Drawer>
  );
};

const tokens = {
  bg: "#F6FBF9",
  card: "#FFFFFF",
  border: "#E2E8F0",
  green: OLIVEEB_COLORS.oliveebGreen,
  greenSoft: OLIVEEB_COLORS.oliveebGreenSoft,
  darkGreen: OLIVEEB_COLORS.oliveebDarkGreen,
  blue: OLIVEEB_COLORS.oliveebBlue,
  blueSoft: OLIVEEB_COLORS.oliveebBlueSoft,
  darkBlue: OLIVEEB_COLORS.oliveebDarkBlue,
  goldSoft: OLIVEEB_COLORS.goldSoft,
  goldBorder: OLIVEEB_COLORS.goldBorder,
  red: OLIVEEB_COLORS.oliveebRed,
  darkRed: OLIVEEB_COLORS.oliveebDarkRed,
  text: "#0F172A",
  muted: "#475569",
};

const landingCss = `
  .olreadia-landing {
    position: relative;
    min-height: 100vh;
    background: #F7FAFC;
    color: ${tokens.text};
    overflow-x: hidden;
  }

  .olreadia-landing::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0) 34%);
  }

  .olreadia-landing > * {
    position: relative;
    z-index: 1;
  }

  .olreadia-landing-shell {
    width: min(1180px, calc(100% - 36px));
    margin: 0 auto;
    padding: 16px 0 30px;
  }

  .olreadia-landing-header {
    position: relative;
    display: grid;
    grid-template-columns: minmax(320px, 520px) minmax(0, 1fr);
    align-items: stretch;
    gap: 22px;
    text-align: left;
    padding: 0 0 22px;
  }

  .olreadia-welcome-brand {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: min(44vw, 520px);
    max-width: 100%;
    aspect-ratio: 1672 / 941;
    padding: 0;
    border: 0;
    border-radius: 14px;
    background: transparent;
    cursor: pointer;
    overflow: hidden;
  }

  .olreadia-welcome-brand img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .olreadia-header-content {
    display: flex;
    min-width: 0;
    flex-direction: column;
    align-items: flex-end;
    justify-content: space-between;
    gap: 18px;
    height: 100%;
    padding: 6px 0;
  }

  .olreadia-promo-row {
    width: min(100%, 620px);
    min-width: 0;
    flex: 1;
    margin: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 134px;
    align-items: stretch;
    gap: 14px;
    padding: 18px;
    border: 1px solid rgba(16, 185, 129, 0.18);
    border-radius: 16px;
    background:
      linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(239,251,255,0.94) 52%, rgba(237,253,245,0.96) 100%);
    box-shadow: 0 16px 34px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.86);
    position: relative;
    overflow: hidden;
  }

  .olreadia-promo-row::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(115deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.62) 42%, rgba(255,255,255,0) 68%);
    transform: translateX(-42%);
  }

  .olreadia-subhead-audience {
    position: relative;
    display: grid;
    align-items: start;
    align-content: start;
    gap: 5px;
    margin: 0;
    color: ${tokens.blue};
    font-size: 16px;
    line-height: 1.34;
    font-style: italic;
    font-weight: 500;
    text-align: left;
    text-shadow: 0 1px 0 rgba(255,255,255,0.75);
    min-width: 0;
  }

  .olreadia-subhead-label {
    color: ${tokens.green};
    font-size: 12px;
    line-height: 1;
    font-family: inherit;
    font-style: normal;
    font-weight: 900;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  .olreadia-promo-button {
    position: relative;
    width: 100%;
    min-width: 0;
    min-height: 38px;
    height: auto;
    align-self: center;
    justify-self: stretch;
    border-radius: 999px;
    padding: 0 10px;
    font-size: 12px;
    font-weight: 900;
    box-shadow: none;
    white-space: normal;
  }

  .olreadia-promo-cta {
    position: relative;
    display: grid;
    align-content: center;
    gap: 14px;
    min-width: 0;
  }

  .olreadia-download-button {
    display: block;
    min-width: 0;
    width: 100%;
    aspect-ratio: 430 / 409;
    padding: 0;
    border-radius: 10px;
    border: 1px solid rgba(0, 128, 255, 0.18);
    background: #FFFFFF;
    box-shadow: 0 10px 20px rgba(0, 58, 112, 0.1);
    overflow: hidden;
  }

  .olreadia-download-button img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .olreadia-header-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
    min-width: 0;
  }

  .olreadia-action-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
  }

  .olreadia-public-nav {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 2px;
  }

  .olreadia-public-nav button,
  .olreadia-public-nav a {
    min-height: 32px;
    border-radius: 999px;
    border: 1px solid #D7EAFE;
    background: #FFFFFF;
    color: ${tokens.darkBlue};
    padding: 5px 11px;
    font-size: 12px;
    font-weight: 900;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    text-decoration: none;
  }

  .olreadia-public-nav button.is-active {
    border-color: ${tokens.green};
    background: ${tokens.green};
    color: #FFFFFF;
  }

  .olreadia-public-nav a {
    border-color: rgba(0, 128, 255, 0.24);
    color: ${tokens.blue};
  }

  .olreadia-public-nav a:hover {
    border-color: ${tokens.blue};
    color: ${tokens.blue};
  }

  .olreadia-pill-button {
    min-width: 112px;
    height: 42px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 900;
  }

  .olreadia-side-cta-row {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
    margin-bottom: 2px;
  }

  .olreadia-side-cta-row .olreadia-pill-button {
    width: 100%;
    min-width: 0;
    height: 40px;
    padding: 0 8px;
    font-size: 12px;
  }

  .olreadia-main-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.65fr) minmax(300px, 0.72fr);
    gap: 18px;
    align-items: stretch;
    margin-top: 14px;
  }

  .olreadia-panel {
    background: #FFFFFF;
    border: 1px solid ${tokens.border};
    border-radius: 12px;
    box-shadow: 0 12px 30px rgba(15,23,42,0.07);
  }

  .olreadia-discovery {
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 18px;
  }

  .olreadia-eyebrow {
    display: inline-flex;
    align-items: center;
    margin: 0 0 12px;
    padding: 8px 14px;
    border-radius: 999px;
    border: 1px solid rgba(39, 174, 48, 0.22);
    background: linear-gradient(135deg, #FFFFFF 0%, ${tokens.greenSoft} 100%);
    color: ${tokens.green};
    font-size: 18px;
    line-height: 1.15;
    font-weight: 950;
    box-shadow: 0 10px 22px rgba(39,174,48,0.12);
  }

  .olreadia-toggle {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
    padding: 5px;
    border-radius: 10px;
    border: 1px solid #E2E8F0;
    background: #F8FAFC;
    box-shadow: none;
    margin-bottom: 14px;
  }

  .olreadia-toggle button {
    min-height: 40px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: transparent;
    color: ${tokens.muted};
    font-size: 13px;
    font-weight: 900;
    cursor: pointer;
  }

  .olreadia-toggle button.is-active {
    color: #FFFFFF;
    background: ${tokens.green};
    border-color: #8DE99A;
    box-shadow: 0 6px 12px rgba(39,174,48,0.12);
  }

  .olreadia-toggle.is-job button.is-active {
    background: ${tokens.blue};
    border-color: ${tokens.blue};
    box-shadow: 0 6px 12px rgba(35,133,219,0.14);
  }

  .olreadia-discovery-heading {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    margin-bottom: 14px;
  }

  .olreadia-discovery-heading .olreadia-section-title {
    margin: 0;
  }

  .olreadia-discovery-heading .olreadia-toggle {
    flex: 0 0 280px;
    margin-bottom: 0;
  }

  .olreadia-section-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
  }

  .olreadia-chip-label {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    border-radius: 999px;
    padding: 4px 9px;
    color: ${tokens.blue};
    background: ${tokens.blueSoft};
    border: 1px solid #D7EAFE;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
  }

  .olreadia-section-title {
    margin: 6px 0 0;
    margin-bottom: 10px;
    color: ${tokens.green};
    font-size: 22px;
    line-height: 1.2;
    font-weight: 900;
  }

  .olreadia-section-text {
    margin: 0 0 12px;
    color: ${tokens.muted};
    font-size: 14px;
    line-height: 1.45;
    font-weight: 400;
  }

  .olreadia-search-box {
    position: relative;
    border: 1px solid rgba(0, 128, 255, 0.18);
    background:
      linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(244,251,255,0.96) 48%, rgba(241,255,247,0.96) 100%);
    border-radius: 16px;
    padding: 14px;
    margin-bottom: 12px;
    box-shadow: 0 18px 42px rgba(0, 58, 112, 0.1), inset 0 1px 0 rgba(255,255,255,0.92);
    overflow: hidden;
  }

  .olreadia-search-box::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(118deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.72) 43%, rgba(255,255,255,0) 68%);
    transform: translateX(-38%);
  }

  .olreadia-search-grid {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(150px, 0.72fr);
    gap: 10px;
  }

  .olreadia-search-grid .ant-select,
  .olreadia-search-grid .ant-select-selector {
    min-width: 0;
  }

  .olreadia-search-field {
    position: relative;
    min-width: 0;
  }

  .olreadia-search-field.ant-input-affix-wrapper {
    min-height: 52px;
    border-radius: 999px;
    border: 1px solid rgba(0, 128, 255, 0.22);
    background: rgba(255,255,255,0.94);
    box-shadow: 0 12px 26px rgba(0, 58, 112, 0.1), inset 0 1px 0 rgba(255,255,255,0.95);
    transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
  }

  .olreadia-search-field.is-location.ant-input-affix-wrapper {
    border-color: rgba(16, 185, 129, 0.24);
  }

  .olreadia-search-field.ant-input-affix-wrapper:hover,
  .olreadia-search-field.ant-input-affix-wrapper-focused {
    border-color: ${tokens.blue} !important;
    box-shadow: 0 0 0 4px rgba(0,128,255,0.1), 0 16px 32px rgba(0, 58, 112, 0.14), inset 0 1px 0 rgba(255,255,255,0.98) !important;
    transform: translateY(-1px);
  }

  .olreadia-search-field.ant-input-affix-wrapper .ant-input,
  .olreadia-search-field.ant-input-affix-wrapper .ant-input::placeholder {
    font-size: 15px;
    font-weight: 800;
  }

  .olreadia-search-field.ant-input-affix-wrapper .ant-input::placeholder {
    color: #64748B;
  }

  .olreadia-search-field.ant-input-affix-wrapper .ant-input-prefix {
    color: ${tokens.green};
    margin-inline-end: 10px;
    font-size: 17px;
  }

  .olreadia-search-field.is-location.ant-input-affix-wrapper .ant-input-prefix {
    color: ${tokens.blue};
  }

  .olreadia-search-clear {
    color: ${tokens.muted};
    border-radius: 999px;
  }

  .olreadia-search-actions {
    position: relative;
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    margin-top: 10px;
  }

  .olreadia-search-submit {
    overflow: hidden;
    border-radius: 999px !important;
    border: 0 !important;
    background: linear-gradient(135deg, ${tokens.blue} 0%, ${tokens.green} 100%) !important;
    box-shadow: 0 14px 30px rgba(0, 128, 255, 0.22), inset 0 1px 0 rgba(255,255,255,0.28);
    transition: transform 160ms ease, box-shadow 160ms ease;
  }

  .olreadia-search-submit:hover {
    transform: translateY(-1px);
    box-shadow: 0 18px 36px rgba(0, 128, 255, 0.28), inset 0 1px 0 rgba(255,255,255,0.32);
  }

  .olreadia-search-submit span:last-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .olreadia-suggestion-block {
    margin-top: 12px;
  }

  .olreadia-search-dropdown {
    border-radius: 12px;
    padding: 6px;
    box-shadow: 0 18px 36px rgba(15,23,42,0.15);
  }

  .olreadia-suggestion-label {
    display: block;
    margin: 0 0 6px;
    color: ${tokens.muted};
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    white-space: nowrap;
  }

  .olreadia-suggestion-label.is-spaced {
    margin-top: 10px;
  }

  .olreadia-chip-row {
    display: flex;
    flex-wrap: nowrap;
    gap: 6px;
    overflow-x: auto;
    padding-bottom: 2px;
  }

  .olreadia-suggestion-chip {
    border: 1px solid #D6F0E1;
    border-radius: 999px;
    background: #FFFFFF;
    color: ${tokens.darkGreen};
    min-height: 30px;
    padding: 5px 10px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    flex: 0 0 auto;
    cursor: pointer;
  }

  .olreadia-suggestion-chip.is-location {
    border-color: #BBDDF8;
    background: ${tokens.blueSoft};
    color: ${tokens.blue};
  }

  .olreadia-suggestion-chip.is-active {
    background: ${tokens.green};
    border-color: ${tokens.green};
    color: #FFFFFF;
  }

  .olreadia-suggestion-chip.is-job.is-active,
  .olreadia-suggestion-chip.is-location.is-active {
    background: ${tokens.blue};
    border-color: ${tokens.blue};
  }

  .olreadia-signals {
    display: grid;
    gap: 8px;
  }

  .olreadia-signal {
    display: grid;
    grid-template-columns: 38px 1fr;
    gap: 10px;
    align-items: center;
    min-height: 54px;
    padding: 9px 12px;
    border-radius: 10px;
    border: 1px solid #D6F0E1;
    background: #F7FFF9;
  }

  .olreadia-signal-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    font-size: 15px;
    color: ${tokens.darkGreen};
    background: ${tokens.greenSoft};
  }

  .olreadia-signal-title {
    display: block;
    color: ${tokens.darkGreen};
    font-size: 13px;
    font-weight: 900;
  }

  .olreadia-signal-text {
    display: block;
    color: ${tokens.muted};
    font-size: 12px;
    line-height: 1.35;
    font-weight: 400;
  }

  .olreadia-empty-state {
    padding: 14px;
    border-radius: 10px;
    border: 1px solid ${tokens.border};
    background: #F8FAFC;
  }

  .olreadia-empty-title {
    display: block;
    color: ${tokens.blue};
    font-size: 15px;
    font-weight: 900;
  }

  .olreadia-empty-text {
    display: block;
    margin-top: 4px;
    color: ${tokens.darkRed};
    font-size: 13px;
    line-height: 1.45;
  }

  .olreadia-empty-help-line {
    display: block;
    margin-top: 8px;
    color: ${tokens.muted};
    line-height: 1.5;
  }

  .olreadia-results-panel {
    margin-top: 14px;
    display: grid;
    gap: 10px;
  }

  .olreadia-results-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 10px;
    flex-wrap: wrap;
  }

  .olreadia-results-title {
    display: block;
    color: ${tokens.text};
    font-size: 18px;
    line-height: 1.25;
    font-weight: 900;
  }

  .olreadia-results-title.is-talent {
    color: ${tokens.blue};
  }

  .olreadia-results-title.is-opportunity {
    color: ${tokens.green};
  }

  .olreadia-results-note {
    display: block;
    margin-top: 4px;
    color: ${tokens.muted};
    font-size: 12px;
    line-height: 1.45;
    font-weight: 400;
  }

  .olreadia-result-count {
    color: ${tokens.blue};
    background: ${tokens.blueSoft};
    border: 1px solid #D7EAFE;
    border-radius: 999px;
    padding: 5px 10px;
    font-size: 11px;
    font-weight: 900;
  }

  .olreadia-result-list {
    display: grid;
    gap: 8px;
  }

  .olreadia-results-pagination {
    display: flex;
    justify-content: flex-end;
    padding-top: 2px;
  }

  .olreadia-results-pagination .ant-pagination-item,
  .olreadia-results-pagination .ant-pagination-prev,
  .olreadia-results-pagination .ant-pagination-next {
    min-width: 30px;
    height: 30px;
    line-height: 28px;
  }

  .olreadia-result-card {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 12px;
    align-items: center;
    padding: 12px;
    border-radius: 10px;
    border: 1px solid #D6F0E1;
    background: #FFFFFF;
    cursor: pointer;
    transition: border-color 160ms ease, box-shadow 160ms ease;
  }

  .olreadia-result-card > div {
    min-width: 0;
  }

  .olreadia-result-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  .olreadia-result-card:hover {
    border-color: ${tokens.green};
    box-shadow: 0 8px 18px rgba(15,23,42,0.07);
  }

  .olreadia-result-card.is-job {
    border-color: #D7EAFE;
  }

  .olreadia-result-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 6px;
  }

  .olreadia-result-pill {
    min-height: 24px;
    max-width: 100%;
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 3px 8px;
    color: ${tokens.green};
    background: ${tokens.greenSoft};
    font-size: 11px;
    font-weight: 900;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .olreadia-result-card.is-job .olreadia-result-pill {
    color: ${tokens.blue};
    background: ${tokens.blueSoft};
  }

  .olreadia-job-drawer .ant-drawer-header {
    border-bottom-color: #E2E8F0;
    padding: 9px 12px;
  }

  .olreadia-job-drawer .ant-drawer-title {
    color: ${tokens.green};
    font-size: 14px;
    font-weight: 900;
  }

  .olreadia-job-drawer .ant-drawer-extra {
    flex: 1;
    display: flex;
    justify-content: flex-end;
    margin-inline-start: 8px;
  }

  .olreadia-job-drawer-header-actions {
    width: 100%;
    justify-content: flex-end;
    overflow-x: auto;
    white-space: nowrap;
  }

  .olreadia-job-drawer .ant-drawer-body {
    padding: 10px 12px;
  }

  .olreadia-job-drawer-content {
    width: 100%;
  }

  .olreadia-job-drawer-empty {
    display: flex;
    min-height: 160px;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .olreadia-job-drawer-hero {
    display: grid;
    gap: 4px;
    padding: 9px;
    border: 1px solid #D6F0E1;
    border-radius: 10px;
    background: linear-gradient(150deg, #FFFFFF, #F3FCF7);
  }

  .olreadia-job-drawer-hero .ant-tag,
  .olreadia-job-drawer-block .ant-tag {
    margin-inline-end: 0;
    font-size: 11px;
    line-height: 18px;
  }

  .olreadia-job-drawer-hero h2 {
    margin: 0;
    color: ${tokens.green};
    font-size: 17px;
    line-height: 1.18;
    font-weight: 950;
  }

  .olreadia-job-drawer-hero .ant-typography {
    font-size: 12px;
  }

  .olreadia-job-drawer-link-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 6px;
  }

  .olreadia-job-drawer-link-row .ant-btn {
    border-radius: 8px;
    font-weight: 900;
  }

  .olreadia-job-drawer-bottom-actions {
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
    justify-content: flex-end;
    overflow-x: auto;
    padding-top: 4px;
  }

  .olreadia-job-drawer-bottom-actions .ant-btn {
    border-radius: 8px;
    font-weight: 900;
  }

  .olreadia-job-drawer-details-button.ant-btn,
  .olreadia-result-details-button.ant-btn {
    background: #FFFFFF;
    border-color: ${tokens.blue};
    color: ${tokens.blue};
  }

  .olreadia-job-drawer-share-button.ant-btn,
  .olreadia-result-share-button.ant-btn {
    background: #FFFFFF;
    border-color: ${tokens.green};
    color: ${tokens.green};
  }

  .olreadia-job-drawer-details-button.ant-btn:hover,
  .olreadia-job-drawer-details-button.ant-btn:focus,
  .olreadia-result-details-button.ant-btn:hover,
  .olreadia-result-details-button.ant-btn:focus {
    background: ${tokens.blueSoft};
    border-color: ${tokens.blue};
    color: ${tokens.blue};
  }

  .olreadia-job-drawer-share-button.ant-btn:hover,
  .olreadia-job-drawer-share-button.ant-btn:focus,
  .olreadia-result-share-button.ant-btn:hover,
  .olreadia-result-share-button.ant-btn:focus {
    background: ${tokens.greenSoft};
    border-color: ${tokens.green};
    color: ${tokens.green};
  }

  .olreadia-job-drawer-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
  }

  .olreadia-job-drawer-fact {
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr);
    gap: 6px;
    align-items: start;
    min-height: 52px;
    padding: 7px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    background: #FFFFFF;
  }

  .olreadia-job-drawer-fact > span {
    display: inline-flex;
    width: 24px;
    height: 24px;
    align-items: center;
    justify-content: center;
    border-radius: 7px;
    background: ${tokens.blueSoft};
    color: ${tokens.blue};
    font-size: 12px;
  }

  .olreadia-job-drawer-fact .ant-typography {
    font-size: 11px;
  }

  .olreadia-job-drawer-fact strong {
    display: block;
    color: ${tokens.text};
    font-size: 12px;
    line-height: 1.22;
  }

  .olreadia-job-drawer-block,
  .olreadia-job-drawer-section {
    display: grid;
    gap: 5px;
    padding: 8px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    background: #FFFFFF;
  }

  .olreadia-job-drawer-block > .ant-typography,
  .olreadia-job-drawer-section > .ant-typography {
    color: ${tokens.darkGreen};
    font-size: 11px;
    font-weight: 900;
  }

  .olreadia-job-drawer-section .olreadia-rich-text {
    color: ${tokens.text};
    font-size: 12px;
    line-height: 1.45;
  }

  .olreadia-job-drawer-poster {
    border-color: #D6F0E1;
    background: #F8FFFA;
  }

  .olreadia-job-drawer-poster strong {
    color: ${tokens.text};
    font-size: 13px;
    line-height: 1.25;
  }

  .olreadia-job-drawer-poster span {
    color: ${tokens.muted};
    font-size: 12px;
    line-height: 1.35;
  }

  .olreadia-result-title {
    display: block;
    color: ${tokens.text};
    font-size: 16px;
    line-height: 1.25;
    font-weight: 900;
  }

  .olreadia-result-subtitle {
    display: block;
    margin-top: 3px;
    color: ${tokens.green};
    font-size: 13px;
    font-weight: 900;
  }

  .olreadia-result-summary {
    display: block;
    margin-top: 6px;
    color: ${tokens.muted};
    font-size: 13px;
    line-height: 1.45;
    font-weight: 400;
  }

  .olreadia-result-skills {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 8px;
  }

  .olreadia-result-skill {
    border-radius: 999px;
    padding: 4px 8px;
    border: 1px solid ${tokens.border};
    color: ${tokens.muted};
    background: #F8FAFC;
    font-size: 11px;
    font-weight: 800;
  }

  .olreadia-choice-stack {
    display: grid;
    gap: 10px;
    position: sticky;
    top: 14px;
  }

  .olreadia-ads-section {
    display: grid;
    gap: 8px;
    padding: 10px;
    border: 1px solid #D7EAFE;
    border-radius: 12px;
    background: #FFFFFF;
    box-shadow: 0 8px 18px rgba(15,23,42,0.05);
  }

  .olreadia-about-section {
    margin-top: 10px;
    padding: 22px;
    border: 1px solid ${tokens.border};
    border-radius: 12px;
    background:
      linear-gradient(135deg, rgba(242,248,255,0.88) 0%, rgba(255,255,255,0.98) 46%, rgba(242,255,247,0.9) 100%);
    box-shadow: 0 14px 32px rgba(15,23,42,0.07);
  }

  .olreadia-about-layout {
    display: grid;
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
    gap: 22px;
    align-items: center;
  }

  .olreadia-about-layout > div {
    min-width: 0;
  }

  .olreadia-about-kicker {
    display: inline-flex;
    align-items: center;
    width: fit-content;
    min-height: 24px;
    margin-bottom: 10px;
    border-radius: 999px;
    padding: 4px 10px;
    border: 1px solid #D7EAFE;
    color: ${tokens.blue};
    background: ${tokens.blueSoft};
    font-size: 10px;
    font-weight: 950;
    text-transform: uppercase;
  }

  .olreadia-about-title {
    margin: 0;
    color: ${tokens.green};
    font-size: 28px;
    line-height: 1.14;
    font-weight: 950;
  }

  .olreadia-about-copy {
    margin: 10px 0 0;
    color: ${tokens.muted};
    font-size: 24px;
    line-height: 1.6;
    font-weight: 500;
  }

  .olreadia-about-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 18px;
  }

  .olreadia-about-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .olreadia-about-media {
    margin: 0 0 12px;
    aspect-ratio: 1829 / 860;
    border-radius: 12px;
    border: 1px solid #D7EAFE;
    background: #FFFFFF;
    box-shadow: 0 14px 30px rgba(15,23,42,0.08);
    overflow: hidden;
  }

  .olreadia-about-media img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .olreadia-about-item {
    min-height: 132px;
    padding: 14px;
    border-radius: 10px;
    border: 1px solid #E2E8F0;
    background: #FFFFFF;
    box-shadow: 0 10px 22px rgba(15,23,42,0.05);
  }

  .olreadia-about-icon {
    display: inline-flex;
    width: 38px;
    height: 38px;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    margin-bottom: 10px;
    color: ${tokens.green};
    background: ${tokens.greenSoft};
    border: 1px solid #D6F0E1;
    font-size: 18px;
  }

  .olreadia-about-item.is-blue .olreadia-about-icon {
    color: ${tokens.blue};
    background: ${tokens.blueSoft};
    border-color: #D7EAFE;
  }

  .olreadia-about-item.is-gold .olreadia-about-icon {
    color: ${tokens.darkRed};
    background: ${tokens.goldSoft};
    border-color: ${tokens.goldBorder};
  }

  .olreadia-about-item-title {
    display: block;
    color: ${tokens.text};
    font-size: 14px;
    line-height: 1.25;
    font-weight: 950;
  }

  .olreadia-about-item-text {
    display: block;
    margin-top: 6px;
    color: ${tokens.muted};
    font-size: 12px;
    line-height: 1.45;
    font-weight: 600;
  }

  .olreadia-ads-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .olreadia-ads-title {
    color: ${tokens.text};
    font-size: 13px;
    line-height: 1.2;
    font-weight: 950;
  }

  .olreadia-ads-label {
    border-radius: 999px;
    padding: 3px 7px;
    border: 1px solid #D7EAFE;
    background: ${tokens.blueSoft};
    color: ${tokens.blue};
    font-size: 9px;
    line-height: 1.1;
    font-weight: 900;
    text-transform: uppercase;
  }

  .olreadia-ad-item {
    display: grid;
    grid-template-columns: 86px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    min-height: 108px;
    padding: 10px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    background: #FFFFFF;
    transition: border-color 180ms ease, background 180ms ease;
  }

  .olreadia-ad-icon {
    display: inline-flex;
    width: 86px;
    height: 86px;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    overflow: hidden;
  }

  .olreadia-ad-icon img {
    display: block;
    width: auto;
    max-width: 100%;
    height: 70px;
    max-height: 70px;
    object-fit: contain;
    padding: 2px;
  }

  .olreadia-ad-item.blue {
    border-color: #D7EAFE;
  }

  .olreadia-ad-item.gold {
    border-color: ${tokens.goldBorder};
  }

  .olreadia-ad-title {
    display: block;
    color: ${tokens.green};
    font-size: 17px;
    line-height: 1.2;
    font-weight: 900;
  }

  .olreadia-ad-text {
    display: block;
    margin-top: 4px;
    color: ${tokens.muted};
    font-size: 13px;
    line-height: 1.34;
  }

  .olreadia-ad-link {
    color: ${tokens.blue};
    font-weight: 950;
    text-decoration: none;
    white-space: nowrap;
  }

  .olreadia-ad-link:hover {
    text-decoration: underline;
  }

  .olreadia-ad-dots {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
  }

  .olreadia-ad-dot {
    width: 7px;
    height: 7px;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: #CBD5E1;
    cursor: pointer;
  }

  .olreadia-ad-dot.is-active {
    width: 18px;
    background: ${tokens.blue};
  }

  .olreadia-choice-card {
    position: relative;
    display: grid;
    grid-template-columns: 48px 1fr 22px;
    gap: 12px;
    align-items: center;
    min-height: 98px;
    padding: 13px;
    border: 1px solid #D6F0E1;
    border-radius: 12px;
    background: #FFFFFF;
    color: inherit;
    text-align: left;
    cursor: pointer;
    box-shadow: 0 8px 18px rgba(15,23,42,0.05);
  }

  .olreadia-choice-card.blue {
    border-color: #D7EAFE;
  }

  .olreadia-choice-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    color: ${tokens.green};
    background: ${tokens.greenSoft};
    font-size: 21px;
  }

  .olreadia-choice-card.blue .olreadia-choice-icon {
    color: ${tokens.blue};
    background: ${tokens.blueSoft};
  }

  .olreadia-choice-title {
    display: block;
    color: ${tokens.text};
    font-size: 17px;
    line-height: 1.25;
    font-weight: 900;
  }

  .olreadia-choice-title strong {
    color: ${tokens.green};
  }

  .olreadia-choice-card.blue .olreadia-choice-title strong {
    color: ${tokens.blue};
  }

  .olreadia-choice-desc {
    display: block;
    margin-top: 5px;
    color: ${tokens.muted};
    font-size: 13px;
    line-height: 1.42;
    font-weight: 400;
  }

  .olreadia-choice-arrow {
    color: ${tokens.blue};
    font-size: 24px;
    font-weight: 900;
  }

  .olreadia-background-check-promo {
    display: grid;
    grid-template-columns: 38px minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
    min-height: 76px;
    padding: 11px;
    border: 1px solid ${tokens.goldBorder};
    border-radius: 12px;
    background: #FFFFFF;
    box-shadow: 0 8px 18px rgba(15,23,42,0.05);
  }

  .olreadia-background-check-icon {
    display: inline-flex;
    width: 38px;
    height: 38px;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: #FFFFFF;
    color: ${tokens.darkRed};
    font-size: 18px;
  }

  .olreadia-background-check-title {
    display: block;
    color: ${tokens.text};
    font-size: 13px;
    line-height: 1.2;
    font-weight: 950;
  }

  .olreadia-background-check-text {
    display: block;
    margin-top: 3px;
    color: ${tokens.muted};
    font-size: 11px;
    line-height: 1.32;
  }

  .olreadia-background-check-action {
    min-width: 72px;
    height: 30px;
    border-radius: 8px;
    border-color: ${tokens.goldBorder};
    color: ${tokens.darkRed};
    background: #FFFFFF;
    font-size: 11px;
    font-weight: 900;
  }

  .olreadia-mini-features {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  .olreadia-mini-features.is-bottom {
    margin-top: 14px;
    padding: 12px;
    border: 1px solid ${tokens.border};
    border-radius: 12px;
    background: #FFFFFF;
    box-shadow: 0 8px 18px rgba(15,23,42,0.05);
  }

  .olreadia-mini-feature {
    min-height: 64px;
    border-radius: 12px;
    border: 1px solid #D6F0E1;
    background: #F7FFF9;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    color: ${tokens.darkGreen};
    font-size: 12px;
    font-weight: 900;
  }

  .olreadia-mini-feature.blue {
    border-color: #D7EAFE;
    background: ${tokens.blueSoft};
    color: ${tokens.blue};
  }

  .olreadia-mini-feature.gold {
    border-color: ${tokens.goldBorder};
    background: ${tokens.goldSoft};
    color: ${tokens.darkRed};
  }

  .olreadia-bottom-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    flex-wrap: wrap;
    padding: 16px;
    margin-top: 16px;
    border-radius: 12px;
    border: 1px solid ${tokens.border};
    background: #FFFFFF;
    box-shadow: 0 8px 18px rgba(15,23,42,0.05);
  }

  .olreadia-public-page {
    margin-top: 16px;
  }

  .olreadia-public-hero {
    padding: 18px;
    border-radius: 12px;
    border: 1px solid ${tokens.border};
    background: #FFFFFF;
    box-shadow: 0 8px 18px rgba(15,23,42,0.05);
  }

  .olreadia-public-kicker {
    display: inline-flex;
    min-height: 24px;
    align-items: center;
    border-radius: 999px;
    padding: 4px 10px;
    border: 1px solid #D7EAFE;
    background: ${tokens.blueSoft};
    color: ${tokens.blue};
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
  }

  .olreadia-public-title {
    margin: 10px 0 0;
    color: ${tokens.darkGreen};
    font-size: 28px;
    line-height: 1.12;
    font-weight: 900;
  }

  .olreadia-public-deck {
    max-width: 920px;
    margin: 10px 0 0;
    color: ${tokens.darkGreen};
    font-size: 15px;
    line-height: 1.55;
    font-weight: 400;
  }

  .olreadia-public-body {
    max-width: 980px;
    margin: 8px 0 0;
    color: ${tokens.muted};
    font-size: 14px;
    line-height: 1.6;
    font-weight: 400;
  }

  .olreadia-public-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-top: 14px;
  }

  .olreadia-public-card {
    min-height: 112px;
    padding: 14px;
    border-radius: 10px;
    border: 1px solid ${tokens.border};
    background: #FFFFFF;
  }

  .olreadia-public-card:nth-child(2) {
    border-color: ${tokens.border};
    background: #FFFFFF;
  }

  .olreadia-public-card:nth-child(3) {
    border-color: ${tokens.border};
    background: #FFFFFF;
  }

  .olreadia-public-card-title {
    display: block;
    color: ${tokens.text};
    font-size: 15px;
    line-height: 1.3;
    font-weight: 900;
  }

  .olreadia-public-card-text {
    display: block;
    margin-top: 6px;
    color: ${tokens.muted};
    font-size: 13px;
    line-height: 1.5;
    font-weight: 400;
  }

  .olreadia-bottom-title {
    margin: 0;
    color: ${tokens.text};
    font-size: 20px;
    font-weight: 900;
  }

  .olreadia-footer {
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid ${tokens.border};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    color: ${tokens.muted};
    font-size: 12px;
  }

  .olreadia-modal-badge {
    display: inline-flex;
    align-items: center;
    min-height: 26px;
    padding: 4px 10px;
    border-radius: 999px;
    border: 1px solid #D7EAFE;
    background: ${tokens.blueSoft};
    color: ${tokens.blue};
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
  }

  .olreadia-modal-title {
    margin: 14px 0 4px;
    color: ${tokens.text};
    font-size: 20px;
    line-height: 1.2;
    font-weight: 900;
  }

  .olreadia-modal-desc {
    margin: 0 0 16px;
    color: ${tokens.muted};
    font-size: 14px;
    line-height: 1.6;
    font-weight: 400;
  }

  .olreadia-share-link {
    padding: 12px;
    border-radius: 12px;
    border: 1px solid ${tokens.border};
    background: #F8FAFC;
    color: ${tokens.blue};
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .olreadia-modal-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 14px;
  }

  @media (max-width: 860px) {
    .olreadia-landing-header {
      grid-template-columns: 1fr;
      align-items: stretch;
      text-align: left;
      gap: 14px;
      padding-bottom: 14px;
    }

    .olreadia-welcome-brand {
      width: min(100%, 520px);
    }

    .olreadia-header-content {
      align-items: stretch;
      justify-content: flex-start;
      height: auto;
      padding: 0;
    }

    .olreadia-header-actions {
      align-items: stretch;
    }

    .olreadia-public-nav {
      justify-content: flex-start;
    }

    .olreadia-main-grid {
      grid-template-columns: 1fr;
      gap: 12px;
    }

    .olreadia-choice-stack {
      position: static;
    }

    .olreadia-promo-row {
      width: 100%;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(118px, 0.5fr);
      text-align: left;
      justify-items: stretch;
      gap: 10px;
      padding: 14px;
    }

    .olreadia-subhead-audience {
      font-size: 15px;
      text-align: left;
    }

    .olreadia-public-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .olreadia-about-layout {
      grid-template-columns: 1fr;
      gap: 16px;
      align-items: stretch;
    }

    .olreadia-about-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .olreadia-bottom-card {
      align-items: flex-start;
    }
  }

  @media (max-width: 560px) {
    .olreadia-landing-shell {
      width: min(100% - 20px, 1380px);
      padding-top: 12px;
    }

    .olreadia-landing-header {
      gap: 12px;
    }

    .olreadia-welcome-brand {
      width: 100%;
      border-radius: 12px;
    }

    .olreadia-action-row,
    .olreadia-bottom-card .olreadia-action-row {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr;
    }

    .olreadia-side-cta-row {
      grid-template-columns: 1fr;
    }

    .olreadia-public-nav {
      display: grid;
      grid-template-columns: 1fr 1fr;
      width: 100%;
    }

    .olreadia-public-nav button,
    .olreadia-public-nav a {
      justify-content: center;
      width: 100%;
      padding-inline: 8px;
      text-align: center;
    }

    .olreadia-pill-button {
      width: 100%;
    }

    .olreadia-promo-button {
      width: 100%;
    }

    .olreadia-promo-row {
      grid-template-columns: 1fr;
      gap: 10px;
      padding: 12px;
      overflow: hidden;
    }

    .olreadia-promo-cta {
      grid-template-columns: 1fr;
    }

    .olreadia-subhead-audience {
      align-items: flex-start;
      gap: 8px;
      font-size: 14px;
      line-height: 1.36;
    }

    .olreadia-public-title {
      font-size: 23px;
    }

    .olreadia-public-hero {
      padding: 14px;
    }

    .olreadia-public-grid {
      grid-template-columns: 1fr;
    }

    .olreadia-discovery-heading {
      align-items: flex-start;
      flex-direction: column;
    }

    .olreadia-discovery-heading .olreadia-toggle {
      width: 100%;
      flex-basis: auto;
    }

    .olreadia-section-head {
      flex-direction: column;
    }

    .olreadia-search-grid {
      grid-template-columns: 1fr;
    }

    .olreadia-search-box {
      padding: 12px;
      border-radius: 14px;
    }

    .olreadia-search-field.ant-input-affix-wrapper {
      min-height: 48px;
    }

    .olreadia-search-field.ant-input-affix-wrapper .ant-input,
    .olreadia-search-field.ant-input-affix-wrapper .ant-input::placeholder {
      font-size: 14px;
    }

    .olreadia-about-section {
      padding: 16px;
    }

    .olreadia-about-layout {
      grid-template-columns: 1fr;
    }

    .olreadia-about-grid {
      grid-template-columns: 1fr;
    }

    .olreadia-about-title {
      font-size: 23px;
    }

    .olreadia-about-copy {
      font-size: 14px;
      line-height: 1.55;
    }

    .olreadia-mini-features {
      grid-template-columns: 1fr;
    }

    .olreadia-result-card {
      grid-template-columns: 1fr;
    }

    .olreadia-result-actions {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr;
    }

    .olreadia-job-drawer-bottom-actions {
      display: flex;
      justify-content: flex-end;
    }

    .olreadia-result-card .ant-btn {
      width: 100%;
      min-width: 0 !important;
      justify-self: stretch;
    }

    .olreadia-results-pagination {
      justify-content: center;
      overflow-x: auto;
      padding-bottom: 2px;
    }

    .olreadia-choice-card {
      grid-template-columns: 44px 1fr 18px;
      padding: 12px;
    }

    .olreadia-choice-icon {
      width: 44px;
      height: 44px;
    }

    .olreadia-choice-title {
      font-size: 16px;
    }

  }
`;

const currentYear = new Date().getFullYear();

const publicNavItems = [
  { key: "home", label: "Home", path: "/" },
  { key: "about", label: "About", path: "/about" },
  { key: "contact", label: "Contact", path: "/contact-us" },
  { key: "privacy", label: "Privacy", path: "/privacy-policy" },
  { key: "help", label: "Help", path: "/help" },
  { key: "terms", label: "Terms", path: "/terms-of-use" },
  { key: "faqs", label: "FAQs", path: "/faqs" },
];

const publicPageContent = {
  home: {
    kicker: "OCarely",
    title: "Care facility operations and care worker tools in one workspace.",
    deck:
      "Run daily care work with resident records, care processes, care plans, schedules, clock-in/out, timesheets, documents, downloads, subscriptions, websites, and AI-assisted care workflows.",
    body:
      "OCarely is built for Adult Family Homes, Assisted Living Facilities, care teams, and care workers who need one organized place for resident onboarding, ongoing care documentation, staff operations, and workspace administration.",
    cards: [
      {
        title: "Resident care operations",
        text: "Manage residents, bio data, contacts, care history, care snapshots, care processes, care plans, daily notes, documents, and care teams.",
      },
      {
        title: "Resident care processes and onboarding",
        text: "Guide new and current residents through state-document review, onboarding, observations, probation, negotiated care planning, and final confirmation.",
      },
      {
        title: "Staff schedules and attendance",
        text: "Create care staff schedules, block active overlaps, track clock-in/out, review timesheets, and keep schedule status visible.",
      },
      {
        title: "Documents, downloads, and websites",
        text: "Store workspace documents, publish downloadable files, manage organization locations, and use annual Standard or Premium plans for a free website.",
      },
      {
        title: "Payments and subscriptions",
        text: "Manage wallet, charges, payment methods, business subscriptions, coupons, billing periods, and workspace plan changes.",
      },
      {
        title: "Premium AI assistance",
        text: "Use Premium tools for AI-assisted care tasks, document extraction, summaries, draft generation, and operational support.",
      },
    ],
  },
  about: {
    kicker: "About OCarely",
    title: "A care operations workspace for facilities and care workers.",
    deck:
      "OCarely helps Adult Family Homes, Assisted Living Facilities, care teams, and care workers coordinate care work from one connected workspace.",
    body:
      "The app brings resident intake, care processes, care plans, schedules, clock-in/out, timesheets, daily notes, documents, downloads, billing, organization locations, and website tools into one organized experience.",
    cards: [
      {
        title: "For care facilities",
        text: "Manage resident records, staff schedules, care processes, care plans, documents, billing, and workspace administration.",
      },
      {
        title: "For care workers",
        text: "Use care worker profiles, schedules, clock-in/out, timesheets, daily notes, and care team workflows.",
      },
      {
        title: "For better care records",
        text: "Keep resident updates, documents, notes, assessment pages, and care-plan changes easier to find and review.",
      },
    ],
  },
  contact: {
    kicker: "Contact Us",
    title: "Talk with Oliveeb about OCarely.",
    deck:
      "Reach out for help with care facility onboarding, resident workflows, staff operations, subscriptions, websites, or care worker accounts.",
    body:
      "OCarely is built for care providers and workers who need a more organized way to run care documentation and daily operations. Contact support if you need help signing in, setting up a workspace, or choosing the right plan.",
    cards: [
      {
        title: "Product support",
        text: "Get help with login, account setup, resident records, care processes, schedules, documents, and downloads.",
      },
      {
        title: "Care facility onboarding",
        text: "Plan how your care facility can use resident care workflows, staff operations, billing, locations, and website tools.",
      },
      {
        title: "Partnership questions",
        text: "Ask about partner programs, referrals, and shared growth opportunities for OCarely.",
      },
    ],
  },
  privacy: {
    kicker: "Privacy Policy",
    title: "Privacy-aware care workflows for resident and staff data.",
    deck:
      "OCarely handles resident, staff, document, schedule, billing, and care workflow information, so access and account context matter.",
    body:
      "Public pages describe the product at a high level. Detailed privacy obligations, data use, account controls, and service-specific terms should be reviewed with the applicable Oliveeb and OCarely agreements for your account or care facility.",
    cards: [
      {
        title: "Account-based access",
        text: "Resident, staff, document, and care workflow details are intended for authenticated user and workspace experiences.",
      },
      {
        title: "Care context",
        text: "Care records help organize resident and staff information while keeping the source and purpose of that context clearer.",
      },
      {
        title: "Review terms",
        text: "Use the published Oliveeb privacy and service documents for formal policy details.",
      },
    ],
  },
  help: {
    kicker: "Help",
    title: "Help for using OCarely before and after sign in.",
    deck:
      "Find the right path for account setup, care facility workspaces, care worker accounts, resident care, schedules, documents, and subscriptions.",
    body:
      "Unlogged-in users can start from the landing page, create an account, or log in. Signed-in users can manage care profiles, workspaces, residents, care processes, care plans, schedules, notes, downloads, billing, partnerships, and support requests from inside the app.",
    cards: [
      {
        title: "Getting started",
        text: "Use Get Started to choose Managing a Care Facility or For my Care Giving Career.",
      },
      {
        title: "Managing daily care",
        text: "Use residents, care processes, care plans, daily notes, documents, schedules, and timesheets from your workspace.",
      },
      {
        title: "Plans and websites",
        text: "Use subscriptions, payment methods, organization locations, downloads, and annual website benefits from workspace admin.",
      },
    ],
  },
  terms: {
    kicker: "Terms of Use",
    title: "Terms for using OCarely services.",
    deck:
      "OCarely usage is governed by the applicable account, product, and service terms provided by Oliveeb.",
    body:
      "These public pages provide a product overview and starting point. Before using account features, resident records, care processes, documents, payments, care facility tools, or workspace workflows, review the applicable terms presented during signup, checkout, or account use.",
    cards: [
      {
        title: "Account responsibility",
        text: "Users are responsible for accurate account, resident, staff, document, billing, and care facility information.",
      },
      {
        title: "Care workflows",
        text: "Resident assessments, care plans, documents, daily notes, schedules, and staff workflows may require additional consent or review.",
      },
      {
        title: "Service updates",
        text: "OCarely features and workflows may evolve as the product and supporting services expand.",
      },
    ],
  },
  faqs: {
    kicker: "FAQs",
    title: "Common questions about OCarely.",
    deck:
      "A quick guide to what OCarely does for care facilities, care workers, and partners.",
    body:
      "OCarely is focused on resident care operations and care worker workflows. It helps facilities manage documentation and staff operations while giving care workers a clearer workspace for schedules, notes, and profile context.",
    cards: [
      {
        title: "Do I need an account?",
        text: "Yes. Care facility workspaces, resident records, schedules, documents, downloads, billing, and care worker tools require an account.",
      },
      {
        title: "What can a care facility manage?",
        text: "Residents, care processes, onboarding, care plans, schedules, clock-in/out, timesheets, daily notes, documents, downloads, locations, subscriptions, and websites.",
      },
      {
        title: "Who uses Olreadia?",
        text: "Adult Family Homes, Assisted Living Facilities, care teams, care workers, and partners who want clearer care operations.",
      },
    ],
  },
};

const pageKeyFromPathname = (pathname) => {
  if (pathname === "/" || pathname === "/product") return "home";
  if (pathname.includes("about")) return "about";
  if (pathname.includes("contact")) return "contact";
  if (pathname.includes("privacy")) return "privacy";
  if (pathname.includes("terms")) return "terms";
  if (pathname.includes("faq")) return "faqs";
  if (pathname.includes("help")) return "help";
  return "home";
};

const PublicProductPage = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [discoveryContext, setDiscoveryContext] = useState("OPPORTUNITIES");
  const [query, setQuery] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [activeLocation, setActiveLocation] = useState("");
  const [searchRan, setSearchRan] = useState(false);
  const [popularSearchOptions, setPopularSearchOptions] = useState([]);
  const [popularLocationOptions, setPopularLocationOptions] = useState([]);
  const [locationApiSuggestions, setLocationApiSuggestions] = useState([]);
  const [locationApiReady, setLocationApiReady] = useState(false);
  const [professionalRoleOptions, setProfessionalRoleOptions] = useState([]);
  const [jobResults, setJobResults] = useState([]);
  const [talentResults, setTalentResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState("");
  const [resultsPage, setResultsPage] = useState(1);
  const [resultsTotal, setResultsTotal] = useState(0);
  const [guestSession, setGuestSession] = useState(null);
  const [talentDrawerOpen, setTalentDrawerOpen] = useState(false);
  const [talentDrawerLoading, setTalentDrawerLoading] = useState(false);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [jobDrawerOpen, setJobDrawerOpen] = useState(false);
  const [jobDrawerLoading, setJobDrawerLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [signInPromptOpen, setSignInPromptOpen] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const discoveryHeadingRef = useRef(null);
  const locationAutocompleteServiceRef = useRef(null);
  const locationFetchSeqRef = useRef(0);

  const isTalent = discoveryContext === "TALENT";
  const googleMapsApiKey =
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.VITE_GOOGLE_MAPS_API_KEY ||
    "";
  const actorId = user?.id || user?.userId || guestSession?.userId || guestSession?.sessionId || "";
  const guestAuthToken = isAuthenticated ? "" : bearerGuestToken(guestSession);
  const pageKey = pageKeyFromPathname(pathname);
  const publicContent = publicPageContent[pageKey];

  const searchPlaceholder = isTalent
    ? "Search talent e.g. nurse, teacher, CPR"
    : "Search jobs e.g. caregiver, assistant, remote";

  const resultSource = isTalent ? talentResults : jobResults;
  const typedQuery = query.trim();
  const shouldShowResults = searchRan;

  useEffect(() => {
    let cancelled = false;

    const initializeGuest = async () => {
      if (isAuthenticated) {
        setGuestSession(null);
        return;
      }
      try {
        const session = await ensureGuestSession();
        if (!cancelled) setGuestSession(session || null);
      } catch {
        if (!cancelled) setGuestSession(null);
      }
    };

    initializeGuest();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    let alive = true;
    setLocationApiReady(false);
    locationAutocompleteServiceRef.current = null;

    ensureGoogleMapsPlacesLoaded(googleMapsApiKey)
      .then((ok) => {
        if (!alive || !ok || !window.google?.maps?.places) return;
        locationAutocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        setLocationApiReady(true);
      })
      .catch(() => {
        if (!alive) return;
        setLocationApiReady(false);
      });

    return () => {
      alive = false;
    };
  }, [googleMapsApiKey]);

  useEffect(() => {
    const value = activeLocation.trim();
    const service = locationAutocompleteServiceRef.current;

    if (!googleMapsApiKey || !locationApiReady || !service || value.length < 2) {
      locationFetchSeqRef.current += 1;
      setLocationApiSuggestions([]);
      return;
    }

    const seq = ++locationFetchSeqRef.current;
    service.getPlacePredictions(
      {
        input: value,
        types: ["(regions)"],
      },
      (predictions) => {
        if (seq !== locationFetchSeqRef.current) return;
        const seen = new Set();
        const next = (Array.isArray(predictions) ? predictions : [])
          .map((item) => String(item?.description || "").trim())
          .filter((item) => {
            const key = item.toLowerCase();
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        setLocationApiSuggestions(next);
      },
    );
  }, [activeLocation, googleMapsApiKey, locationApiReady]);

  useEffect(() => {
    let cancelled = false;

    const preloadSuggestions = async () => {
      if (!isAuthenticated && !guestAuthToken) return;
      try {
        const [remoteSearches, remoteLocations] = await Promise.all([
          olreadiaService.appStart.popularSearches({ actorId, authToken: guestAuthToken }),
          olreadiaService.appStart.popularLocations({ actorId, authToken: guestAuthToken }),
        ]);
        if (cancelled) return;

        setPopularSearchOptions(
          uniqueSuggestionValues((remoteSearches || []).map(popularSearchLabel)).map((value) => ({
            value,
            label: value,
          })),
        );
        setPopularLocationOptions(
          uniqueSuggestionValues((remoteLocations || []).map(popularLocationLabel)).map((value) => ({
            value,
            label: value,
          })),
        );
      } catch {
        if (!cancelled) {
          setPopularSearchOptions([]);
          setPopularLocationOptions([]);
        }
      }
    };

    preloadSuggestions();
    return () => {
      cancelled = true;
    };
  }, [actorId, guestAuthToken, isAuthenticated]);

  useEffect(() => {
    let cancelled = false;

    const loadProfessionalRoles = async () => {
      if (!isAuthenticated && !guestAuthToken) return;
      try {
        const roles = await olreadiaService.professionRoles.list(actorId, guestAuthToken);
        if (cancelled) return;
        setProfessionalRoleOptions(roles || []);
      } catch {
        if (!cancelled) setProfessionalRoleOptions([]);
      }
    };

    loadProfessionalRoles();
    return () => {
      cancelled = true;
    };
  }, [actorId, guestAuthToken, isAuthenticated]);

  const popularSearchValues = useMemo(
    () => popularSearchOptions.map((item) => item.value),
    [popularSearchOptions],
  );

  const popularLocationValues = useMemo(
    () => popularLocationOptions.map((item) => item.value),
    [popularLocationOptions],
  );

  const searchSuggestions = useMemo(() => {
    return roleOptionsFor(professionalRoleOptions, typedQuery);
  }, [professionalRoleOptions, typedQuery]);

  const locationSuggestions = useMemo(() => {
    const uniqueLocations = uniqueSuggestionValues(popularLocationValues);
    const normalizedLocation = activeLocation.trim().toLowerCase();

    if (!normalizedLocation) {
      return uniqueLocations.slice(0, 8);
    }

    if (googleMapsApiKey && normalizedLocation.length >= 2) {
      return locationApiSuggestions.slice(0, 8);
    }

    const matches = uniqueLocations.filter((item) =>
      item.toLowerCase().includes(normalizedLocation),
    );

    return matches.length > 0 ? matches.slice(0, 8) : uniqueLocations.slice(0, 4);
  }, [activeLocation, googleMapsApiKey, locationApiSuggestions, popularLocationValues]);

  const visibleResults = useMemo(() => {
    if (!shouldShowResults) {
      return [];
    }
    return resultSource;
  }, [resultSource, shouldShowResults]);

  const searchButtonLabel = typedQuery
    ? isTalent
      ? `Search "${typedQuery}"`
      : `Browse "${typedQuery}" jobs`
    : isTalent
      ? "Search Talent"
      : "Browse Current Jobs";

  const goToLogin = () => navigate(paths.simpleLogin);
  const goToSignup = () =>
    navigate(paths.getStarted, { state: { source: "Landing", mode: "GET_HELP" } });

  const openAccountPrompt = () => setSignInPromptOpen(true);

  const focusDiscoveryHeading = () => {
    window.requestAnimationFrame(() => {
      discoveryHeadingRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
      discoveryHeadingRef.current?.focus({ preventScroll: true });
    });
  };

  const requestIdentity = async () => {
    let requestActorId = actorId;
    let requestAuthToken = guestAuthToken;

    if (!isAuthenticated && !requestAuthToken) {
      const session = await ensureGuestSession();
      setGuestSession(session || null);
      requestActorId = session?.userId || session?.sessionId || "";
      requestAuthToken = bearerGuestToken(session);
    }

    return { requestActorId, requestAuthToken };
  };

  const loadLandingResults = async (page = 1, totalCap = null) => {
    try {
      const { requestActorId, requestAuthToken } = await requestIdentity();
      if (!isAuthenticated && !requestAuthToken) return;
      setResultsLoading(true);
      setResultsError("");
      setResultsPage(page);

      if (isTalent) {
        const talentPage = await olreadiaService.professionProfiles.searchTalent({
          actorId: requestActorId,
          authToken: requestAuthToken,
          query: [typedQuery, activeLocation.trim()].filter(Boolean).join(" "),
          page: page - 1,
          size: RESULTS_PAGE_SIZE,
        });
        const talentCards = pageContent(talentPage).map(talentCardFromApi).filter((item) => item.id);
        if (page > 1 && talentCards.length === 0) {
          const cappedTotal = (page - 1) * RESULTS_PAGE_SIZE;
          setResultsTotal(cappedTotal);
          await loadLandingResults(page - 1, cappedTotal);
          return;
        }
        setTalentResults(talentCards);
        const nextTotal = renderedPageTotal(talentPage, talentCards, page);
        setResultsTotal(totalCap == null ? nextTotal : Math.min(nextTotal, totalCap));
        return;
      }

      const jobsPage = await olreadiaService.jobOpportunities.listRecent({
        actorId: requestActorId,
        authToken: requestAuthToken,
        search: [typedQuery, activeLocation.trim()].filter(Boolean).join(" "),
        page: page - 1,
        size: RESULTS_PAGE_SIZE,
      });
      const jobCards = pageContent(jobsPage).map(jobCardFromApi).filter((item) => item.id);
      if (page > 1 && jobCards.length === 0) {
        const cappedTotal = (page - 1) * RESULTS_PAGE_SIZE;
        setResultsTotal(cappedTotal);
        await loadLandingResults(page - 1, cappedTotal);
        return;
      }
      setJobResults(jobCards);
      const nextTotal = renderedPageTotal(jobsPage, jobCards, page);
      setResultsTotal(totalCap == null ? nextTotal : Math.min(nextTotal, totalCap));
    } catch (nextError) {
      if (isTalent) {
        setTalentResults([]);
      } else {
        setJobResults([]);
      }
      setResultsTotal(0);
      setResultsError(nextError?.message || "Could not load public results.");
    } finally {
      setResultsLoading(false);
    }
  };

  const handleSearchSubmit = async () => {
    setSearchRan(true);
    await loadLandingResults(1);
    focusDiscoveryHeading();
  };

  const handleResultsPageChange = async (page) => {
    setSearchRan(true);
    await loadLandingResults(page);
  };

  const handleQueryChange = (value) => {
    setQuery(value);
    setActiveSearch("");
    setSearchDropdownOpen(Boolean(value.trim()));
    setResultsPage(1);
    setResultsTotal(0);
    setSearchRan(false);
    setResultsError("");
  };

  const handleLocationChange = (value) => {
    setActiveLocation(value);
    setResultsPage(1);
    setResultsTotal(0);
    setSearchRan(false);
    setResultsError("");
  };

  const handleSuggestion = (value) => {
    const nextValue = activeSearch === value ? "" : value;
    setActiveSearch(nextValue);
    setQuery(nextValue);
    setSearchDropdownOpen(false);
    setResultsPage(1);
    setResultsTotal(0);
    setSearchRan(false);
    setResultsError("");
  };

  const handleLocation = (value) => {
    const nextValue = activeLocation === value ? "" : value;
    setActiveLocation(nextValue);
    setResultsPage(1);
    setResultsTotal(0);
    setSearchRan(false);
    setResultsError("");
  };

  const clearSearch = () => {
    setQuery("");
    setActiveSearch("");
    setActiveLocation("");
    setSearchDropdownOpen(false);
    setResultsPage(1);
    setResultsTotal(0);
    setSearchRan(false);
    setResultsError("");
    setJobResults([]);
    setTalentResults([]);
  };

  const closeTalentDrawer = () => {
    setTalentDrawerOpen(false);
    setSelectedTalent(null);
    setTalentDrawerLoading(false);
  };

  const closeJobDrawer = () => {
    setJobDrawerOpen(false);
    setSelectedJob(null);
    setJobDrawerLoading(false);
  };

  const openTalentDrawer = async (item) => {
    if (!item?.id) return;
    setTalentDrawerOpen(true);
    setTalentDrawerLoading(true);
    setSelectedTalent(item);
    try {
      const { requestAuthToken } = await requestIdentity();
      if (!isAuthenticated && !requestAuthToken) return;
      const fullProfile = await olreadiaService.professionProfiles.get(item.id, requestAuthToken);
      setSelectedTalent(fullProfile || item);
    } catch (nextError) {
      message.error(nextError?.message || "Could not load talent details.");
    } finally {
      setTalentDrawerLoading(false);
    }
  };

  const openJobDrawer = async (item) => {
    if (!item?.id) return;
    setJobDrawerOpen(true);
    setJobDrawerLoading(true);
    setSelectedJob(null);
    try {
      const { requestActorId, requestAuthToken } = await requestIdentity();
      if (!isAuthenticated && !requestAuthToken) return;
      const fullJob = await olreadiaService.jobOpportunities.get(item.id, requestActorId, requestAuthToken);
      setSelectedJob(fullJob);
    } catch (nextError) {
      message.error(nextError?.message || "Could not load job details.");
    } finally {
      setJobDrawerLoading(false);
    }
  };

  const applyFromJobDrawer = () => {
    if (!isAuthenticated) {
      setSignInPromptOpen(true);
      return;
    }
    const jobId = selectedJob?.id || selectedJob?.jobOpportunityId;
    if (jobId) navigate(`${paths.jobApplications}?jobOpportunityId=${encodeURIComponent(jobId)}`);
  };

  const contactFromTalentDrawer = () => {
    setSignInPromptOpen(true);
  };

  const openResultDetails = (item) => {
    if (!item?.id) return;
    if (isTalent) {
      openTalentDrawer(item);
      return;
    }
    openJobDrawer(item);
  };

  const jobDetailsPath = (item) => {
    const jobId = item?.jobOpportunityId || item?.id;
    return jobId ? paths.jobOpportunityDetails(encodeURIComponent(jobId)) : "";
  };

  const openJobDetailsPage = (item) => {
    const path = jobDetailsPath(item);
    if (path) window.open(path, "_blank", "noopener,noreferrer");
  };

  const shareJobDetails = async (item) => {
    const path = jobDetailsPath(item);
    if (!path) {
      message.warning("This job is missing a shareable details link.");
      return;
    }

    const url = `${window.location.origin}${path}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: item?.title || "Olreadia job opportunity",
          url,
        });
        return;
      }

      await navigator.clipboard.writeText(url);
      message.success("Job details link copied.");
    } catch (nextError) {
      if (nextError?.name !== "AbortError") {
        message.error(nextError?.message || "Unable to share this job right now.");
      }
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(APP_SHARE_LINK);
      message.success("Share link copied.");
    } catch {
      message.error("Unable to copy link.");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      await copyShareLink();
      return;
    }

    try {
      await navigator.share({
        title: "Share Olreadia",
        text: APP_SHARE_MESSAGE,
        url: APP_SHARE_LINK,
      });
    } catch (error) {
      if (error?.name !== "AbortError") {
        message.error("Unable to share right now.");
      }
    }
  };

  const closeSignInPrompt = () => setSignInPromptOpen(false);

  return (
    <div className="olreadia-landing">
      <style>{landingCss}</style>
      <div className="olreadia-landing-shell">
        <header className="olreadia-landing-header">
          <button
            type="button"
            className="olreadia-welcome-brand"
            onClick={() => navigate("/")}
            aria-label="Go to Olreadia home"
          >
            <img src="/welcome.png" alt="Olreadia" />
          </button>

          <div className="olreadia-header-content">
            <nav className="olreadia-public-nav" aria-label="Public pages">
              {publicNavItems.map((item) => (
                <button
                  type="button"
                  key={item.key}
                  className={pageKey === item.key ? "is-active" : ""}
                  onClick={() => navigate(item.path)}
                >
                  {item.label}
                </button>
              ))}
              {/* <a href={IOS_DOWNLOAD_LINK} aria-label="Download Olreadia for iOS">
                <AppleOutlined />
                <span>iOS</span>
              </a>
              <a href={ANDROID_DOWNLOAD_LINK} aria-label="Download Olreadia for Android">
                <AndroidOutlined />
                <span>Android</span>
              </a> */}
            </nav>

            <div className="olreadia-promo-row">
              <span className="olreadia-subhead-audience">
                <span className="olreadia-subhead-label">For care facilities,</span>
                <span>Manage residents, care processes, care plans, schedules, attendance, documents, billing, and websites from one workspace.</span>
              </span>
              <span className="olreadia-subhead-audience">
                <span className="olreadia-subhead-label">For care workers,</span>
                <span>Use your care profile, schedules, clock-in/out, timesheets, daily notes, and care team tools in one place.</span>
              </span>
              <span className="olreadia-promo-cta">
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  className="olreadia-promo-button"
                  onClick={goToSignup}
                  style={{
                    background: tokens.green,
                    borderColor: tokens.green,
                  }}
                >
                  Get Started
                </Button>
                <a className="olreadia-download-button" href={DOWNLOAD_PAGE_LINK} aria-label="Download Olreadia app">
                  <img src="/app-download.jpg" alt="" />
                </a>
              </span>
            </div>
          </div>
        </header>

        <main className="olreadia-main-grid">
          <section className="olreadia-panel olreadia-discovery">
            {publicContent ? (
              <>
                <p className="olreadia-section-title">OCarely offerings</p>
                <section className="olreadia-public-hero">
                  <span className="olreadia-public-kicker">{publicContent.kicker}</span>
                  <h1 className="olreadia-public-title">{publicContent.title}</h1>
                  <p className="olreadia-public-deck">{publicContent.deck}</p>
                  <p className="olreadia-public-body">{publicContent.body}</p>
                  <div className="olreadia-public-grid">
                    {publicContent.cards.map((card) => (
                      <article className="olreadia-public-card" key={card.title}>
                        <Text className="olreadia-public-card-title">{card.title}</Text>
                        <Text className="olreadia-public-card-text">{card.text}</Text>
                      </article>
                    ))}
                  </div>
                  <div className="olreadia-modal-actions">
                    <Button
                      type="primary"
                      icon={<UserAddOutlined />}
                      size="large"
                      onClick={goToSignup}
                      style={{
                        borderRadius: 999,
                        background: tokens.green,
                        borderColor: tokens.green,
                        fontWeight: 900,
                      }}
                    >
                      Get Started
                    </Button>
                    <Button
                      icon={<LoginOutlined />}
                      size="large"
                      onClick={goToLogin}
                      style={{
                        borderRadius: 999,
                        color: tokens.green,
                        borderColor: tokens.green,
                        fontWeight: 900,
                      }}
                    >
                      Login
                    </Button>
                  </div>
                </section>
              </>
            ) : (
              <>
                <div className="olreadia-discovery-heading">
                  <p className="olreadia-section-title" ref={discoveryHeadingRef} tabIndex={-1}>
                    What can we help you find today?
                  </p>
                  <div className={`olreadia-toggle${isTalent ? "" : " is-job"}`} role="tablist" aria-label="Discovery mode">
                    <button
                      type="button"
                      className={isTalent ? "is-active" : ""}
                      onClick={() => {
                        setDiscoveryContext("TALENT");
                        clearSearch();
                      }}
                    >
                      Find Talent
                    </button>
                    <button
                      type="button"
                      className={!isTalent ? "is-active" : ""}
                      onClick={() => {
                        setDiscoveryContext("OPPORTUNITIES");
                        clearSearch();
                      }}
                    >
                      Search Jobs
                    </button>
                  </div>
                </div>

            {/* <div className="olreadia-section-head">
              <div>
                <span className="olreadia-chip-label">
                  {isTalent ? "For employers" : "For professionals"}
                </span>
                {isTalent ? <h1 className="olreadia-section-title">Verified talent pool</h1> : null}
              </div>
              <span className="olreadia-chip-label">
                {isTalent ? "Live profiles" : "Open roles"}
              </span>
            </div> */}

            <p className="olreadia-section-text">
              {isTalent
                ? "Search by job title, skill, service, or profession."
                : "Search by job title, service, task type, or keyword."}
            </p>

            <div className="olreadia-search-box">
              <div className="olreadia-search-grid">
                <AutoComplete
                  allowClear={false}
                  value={query}
                  onChange={handleQueryChange}
                  onSelect={(value) => {
                    setQuery(value);
                    setActiveSearch(value);
                    setSearchDropdownOpen(false);
                    setResultsPage(1);
                    setResultsTotal(0);
                    setSearchRan(false);
                    setResultsError("");
                  }}
                  options={searchSuggestions}
                  filterOption={false}
                  open={searchDropdownOpen && typedQuery.length > 0 && searchSuggestions.length > 0}
                  popupClassName="olreadia-search-dropdown"
                  style={{ width: "100%" }}
                >
                  <Input
                    size="large"
                    onPressEnter={handleSearchSubmit}
                    placeholder={searchPlaceholder}
                    prefix={<SearchOutlined />}
                    suffix={
                      query ? (
                        <Button
                          type="text"
                          size="small"
                          className="olreadia-search-clear"
                          icon={<CloseOutlined />}
                          onClick={clearSearch}
                          aria-label="Clear search"
                        />
                      ) : null
                    }
                    className="olreadia-search-field"
                  />
                </AutoComplete>

                <AutoComplete
                  allowClear={false}
                  value={activeLocation}
                  onChange={handleLocationChange}
                  onSelect={(value) => {
                    setActiveLocation(value);
                    setResultsPage(1);
                    setResultsTotal(0);
                    setSearchRan(false);
                    setResultsError("");
                  }}
                  options={locationSuggestions.map((item) => ({ value: item, label: item }))}
                  filterOption={false}
                  popupClassName="olreadia-search-dropdown"
                  style={{ width: "100%" }}
                >
                  <Input
                    size="large"
                    onPressEnter={handleSearchSubmit}
                    placeholder="Location or remote"
                    prefix={<EnvironmentOutlined />}
                    suffix={
                      activeLocation ? (
                        <Button
                          type="text"
                          size="small"
                          className="olreadia-search-clear"
                          icon={<CloseOutlined />}
                          onClick={() => {
                            setActiveLocation("");
                            setResultsPage(1);
                            setResultsTotal(0);
                            setSearchRan(false);
                            setResultsError("");
                          }}
                          aria-label="Clear location"
                        />
                      ) : null
                    }
                    className="olreadia-search-field is-location"
                  />
                </AutoComplete>
              </div>

              <div className="olreadia-search-actions">
                <Button
                  type="primary"
                  size="large"
                  icon={<SearchOutlined />}
                  className="olreadia-search-submit"
                  onClick={handleSearchSubmit}
                  title={searchButtonLabel}
                  style={{
                    height: 42,
                    borderRadius: 10,
                    background: tokens.blue,
                    borderColor: tokens.blue,
                    fontSize: 14,
                    fontWeight: 900,
                  }}
                >
                  {searchButtonLabel}
                </Button>
              </div>

              <div className="olreadia-suggestion-block">
                <Text className="olreadia-suggestion-label">Popular searches</Text>
                <div className="olreadia-chip-row">
                  {popularSearchValues.slice(0, 8).map((item) => (
                    <button
                      type="button"
                      key={`${discoveryContext}-${item}`}
                      className={`olreadia-suggestion-chip${isTalent ? "" : " is-job"}${activeSearch === item ? " is-active" : ""}`}
                      onClick={() => handleSuggestion(item)}
                    >
                      {item}
                    </button>
                  ))}
                </div>

                <Text className="olreadia-suggestion-label is-spaced">Popular locations</Text>
                <div className="olreadia-chip-row">
                  {popularLocationValues.slice(0, 8).map((item) => (
                    <button
                      type="button"
                      key={item}
                      className={`olreadia-suggestion-chip is-location${activeLocation === item ? " is-active" : ""}`}
                      onClick={() => handleLocation(item)}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="olreadia-results-panel">
              <div className="olreadia-results-head">
                <span>
                  <Text className={`olreadia-results-title${isTalent ? " is-talent" : " is-opportunity"}`}>
                    {isTalent ? "Talent search results" : "Opportunity search results"}
                  </Text>
                  {/* <Text className="olreadia-results-note">
                    {shouldShowResults
                      ? "Public results are visible now. Login is only required to apply or contact talent."
                      : "Type to see role suggestions, then search to load current posts."}
                  </Text> */}
                </span>
                {shouldShowResults ? (
                  <span className="olreadia-result-count">
                    {resultsLoading
                      ? "Loading"
                      : `${resultsTotal || visibleResults.length} ${(resultsTotal || visibleResults.length) === 1 ? "result" : "results"}`}
                  </span>
                ) : null}
              </div>

              <div className="olreadia-result-list">
                {resultsError ? (
                  <div className="olreadia-empty-state">
                    <Text className="olreadia-empty-title">Unable to load public results</Text>
                    <Text className="olreadia-empty-text">{resultsError}</Text>
                  </div>
                ) : visibleResults.length > 0 ? (
                  visibleResults.map((item) => (
                    <article
                      className={`olreadia-result-card${isTalent ? "" : " is-job"}`}
                      key={`${discoveryContext}-${item.id}`}
                      onClick={() => openResultDetails(item)}
                    >
                      <div>
                        <div className="olreadia-result-meta">
                          <span className="olreadia-result-pill">
                            <EnvironmentOutlined />&nbsp;{item.location}
                          </span>
                          {!isTalent && item.postedDate ? (
                            <span className="olreadia-result-pill">
                              <CalendarOutlined />&nbsp;Posted {item.postedDate}
                            </span>
                          ) : null}
                          <span className="olreadia-result-pill">{item.type}</span>
                          {!isTalent && item.specialization ? (
                            <span className="olreadia-result-pill">{item.specialization}</span>
                          ) : null}
                          <span className="olreadia-result-pill">{item.badge}</span>
                        </div>
                        <Text className="olreadia-result-title">{item.title}</Text>
                        {isTalent ? <Text className="olreadia-result-subtitle">{item.organization}</Text> : null}
                        {isTalent ? <Text className="olreadia-result-summary">{item.summary}</Text> : null}
                        <div className="olreadia-result-skills">
                          {item.skills.map((skill) => (
                            <span className="olreadia-result-skill" key={skill}>
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="olreadia-result-actions">
                        {isTalent ? (
                          <Button
                            icon={<FileDoneOutlined />}
                            onClick={(event) => {
                              event.stopPropagation();
                              openResultDetails(item);
                            }}
                            style={{
                              minWidth: 104,
                              height: 38,
                              borderRadius: 10,
                              borderColor: tokens.blue,
                              color: tokens.blue,
                              fontWeight: 900,
                            }}
                          >
                            View details
                          </Button>
                        ) : (
                          <>
                            <Button
                              className="olreadia-result-details-button"
                              icon={<FileDoneOutlined />}
                              onClick={(event) => {
                                event.stopPropagation();
                                openResultDetails(item);
                              }}
                              style={{
                                minWidth: 96,
                                height: 38,
                                borderRadius: 10,
                                fontWeight: 900,
                              }}
                            >
                              Details
                            </Button>
                            <Button
                              className="olreadia-result-share-button"
                              icon={<ShareAltOutlined />}
                              onClick={(event) => {
                                event.stopPropagation();
                                shareJobDetails(item);
                              }}
                              style={{
                                minWidth: 92,
                                height: 38,
                                borderRadius: 10,
                                fontWeight: 900,
                              }}
                            >
                              Share
                            </Button>
                          </>
                        )}
                      </div>
                    </article>
                  ))
                ) : resultsLoading ? (
                  <div className="olreadia-empty-state">
                    <Text className="olreadia-empty-title">Loading public results...</Text>
                    <Text className="olreadia-empty-text">Fetching the latest records from Olreadia.</Text>
                  </div>
                ) : (
                  <div className="olreadia-empty-state">
                    <Text className="olreadia-empty-title">
                      {shouldShowResults ? "No public matches yet" : "Search to see jobs"}
                    </Text>
                    <Text className="olreadia-empty-text">
                      {shouldShowResults
                        ? "Try a broader keyword or choose one of the popular searches above."
                        : (
                          <>
                            Enter a role, skill, service, or location to load open opportunities.
                            <span className="olreadia-empty-help-line">
                              Try searches like Registered Nurse, Software Engineer, Caregiver,
                              Project Manager, Plumber, or Customer Support.
                            </span>
                            <span className="olreadia-empty-help-line">
                              Add a city, state, or nearby area to narrow results, then use the
                              popular searches and locations above to move faster.
                            </span>
                          </>
                        )}
                    </Text>
                  </div>
                )}
              </div>
              {shouldShowResults && !resultsError && visibleResults.length > 0 && resultsTotal > RESULTS_PAGE_SIZE ? (
                <div className="olreadia-results-pagination">
                  <Pagination
                    size="small"
                    current={resultsPage}
                    pageSize={RESULTS_PAGE_SIZE}
                    total={resultsTotal}
                    showSizeChanger={false}
                    disabled={resultsLoading}
                    onChange={handleResultsPageChange}
                  />
                </div>
              ) : null}
            </div>

            {isTalent && (
              <div className="olreadia-signals">
                <div className="olreadia-signal">
                  <span className="olreadia-signal-icon">
                    <SafetyCertificateOutlined />
                  </span>
                  <span>
                    <Text className="olreadia-signal-title">Screened</Text>
                    <Text className="olreadia-signal-text">Identity and background</Text>
                  </span>
                </div>
                <div className="olreadia-signal">
                  <span className="olreadia-signal-icon">
                    <AimOutlined />
                  </span>
                  <span>
                    <Text className="olreadia-signal-title">Matched</Text>
                    <Text className="olreadia-signal-text">Skills and location fit</Text>
                  </span>
                </div>
                <div className="olreadia-signal">
                  <span className="olreadia-signal-icon">
                    <FileDoneOutlined />
                  </span>
                  <span>
                    <Text className="olreadia-signal-title">Ready</Text>
                    <Text className="olreadia-signal-text">Docs and references</Text>
                  </span>
                </div>
              </div>
            )}
              </>
            )}
          </section>

          <aside className="olreadia-choice-stack">
            <div className="olreadia-side-cta-row">
              <Button
                type="primary"
                icon={<LoginOutlined />}
                className="olreadia-pill-button"
                onClick={goToLogin}
                style={{
                  background: tokens.green,
                  borderColor: tokens.green,
                }}
              >
                Login
              </Button>
              <Button
                icon={<UserAddOutlined />}
                className="olreadia-pill-button"
                onClick={goToSignup}
                style={{
                  color: tokens.green,
                  background: tokens.greenSoft,
                  borderColor: "#D6F0E1",
                }}
              >
                Get Started
              </Button>
              <Button
                icon={<ShareAltOutlined />}
                className="olreadia-pill-button"
                onClick={() => setShareOpen(true)}
                style={{
                  color: tokens.blue,
                  background: tokens.blueSoft,
                  borderColor: "#D7EAFE",
                }}
              >
                Share
              </Button>
            </div>

            <OliveebProductAds className="olreadia-ads-section" intervalMs={4200} />

            <button
              type="button"
              className="olreadia-choice-card"
              onClick={openAccountPrompt}
            >
              <span className="olreadia-choice-icon">
                <CheckCircleOutlined />
              </span>
              <span>
                <Text className="olreadia-choice-title">
                  Manage <strong>resident care</strong> workflows
                </Text>
                <Text className="olreadia-choice-desc">
                  Track resident records, assessments, care plans, daily notes, documents, and care teams.
                </Text>
              </span>
              <span className="olreadia-choice-arrow">›</span>
            </button>

            <button
              type="button"
              className="olreadia-choice-card blue"
              onClick={openAccountPrompt}
            >
              <span className="olreadia-choice-icon">
                <IdcardOutlined />
              </span>
              <span>
                <Text className="olreadia-choice-title">
                  Run <strong>staff operations</strong>
                </Text>
                <Text className="olreadia-choice-desc">
                  Create schedules, prevent active overlaps, manage clock-in/out, and review timesheets.
                </Text>
              </span>
              <span className="olreadia-choice-arrow">›</span>
            </button>

            <section className="olreadia-background-check-promo" aria-label="Premium AI promotion">
              <span className="olreadia-background-check-icon">
                <SafetyCertificateOutlined />
              </span>
              <span>
                <Text className="olreadia-background-check-title">Premium AI tools</Text>
                <Text className="olreadia-background-check-text">
                  Use AI-assisted document extraction, summaries, drafts, and care workflow support.
                </Text>
              </span>
              <Button className="olreadia-background-check-action" onClick={() => setPremiumOpen(true)}>
                Learn
              </Button>
            </section>
          </aside>
        </main>

        <section className="olreadia-about-section" aria-label="About Olreadia">
          <div className="olreadia-about-layout">
            <div>
              <span className="olreadia-about-kicker">About OCarely</span>
              <h2 className="olreadia-about-title">A calmer operating system for care facilities.</h2>
              <p className="olreadia-about-copy">
                OCarely helps Adult Family Homes, Assisted Living Facilities, care teams, and
                care workers keep the daily work of care organized. Resident records, assessments,
                care plans, documents, schedules, attendance, notes, billing, and websites live in
                one workspace.
              </p>
              <p className="olreadia-about-copy">
                Free plans cover core setup, Standard adds schedules, daily notes, assessments, and
                annual website benefits, and Premium adds AI-assisted care tasks, summaries, and
                document extraction.
              </p>
              <div className="olreadia-about-actions">
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  className="olreadia-pill-button"
                  onClick={goToSignup}
                  style={{ background: tokens.green, borderColor: tokens.green }}
                >
                  Get Started
                </Button>
                <Button
                  icon={<LoginOutlined />}
                  className="olreadia-pill-button"
                  onClick={goToLogin}
                  style={{
                    color: tokens.blue,
                    background: tokens.blueSoft,
                    borderColor: "#D7EAFE",
                  }}
                >
                  Login
                </Button>
              </div>
            </div>

            <div>
              <figure className="olreadia-about-media">
                <img src="/team.png" alt="Olreadia team collaboration" width="1829" height="860" loading="lazy" />
              </figure>
              <div className="olreadia-about-grid">
                <article className="olreadia-about-item">
                  <span className="olreadia-about-icon"><IdcardOutlined /></span>
                  <Text className="olreadia-about-item-title">Resident records</Text>
                  <Text className="olreadia-about-item-text">
                    Keep bio data, contacts, care history, documents, assessments, and care plans together.
                  </Text>
                </article>
                <article className="olreadia-about-item is-blue">
                  <span className="olreadia-about-icon"><SearchOutlined /></span>
                  <Text className="olreadia-about-item-title">Schedules and attendance</Text>
                  <Text className="olreadia-about-item-text">
                    Manage staff schedules, clock-in/out, timesheets, and schedule status in the workspace.
                  </Text>
                </article>
                <article className="olreadia-about-item is-gold">
                  <span className="olreadia-about-icon"><SafetyCertificateOutlined /></span>
                  <Text className="olreadia-about-item-title">Documents and downloads</Text>
                  <Text className="olreadia-about-item-text">
                    Upload workspace documents, preview files, and publish internal or public downloads.
                  </Text>
                </article>
                <article className="olreadia-about-item is-blue">
                  <span className="olreadia-about-icon"><SolutionOutlined /></span>
                  <Text className="olreadia-about-item-title">Plans, websites, and AI</Text>
                  <Text className="olreadia-about-item-text">
                    Manage subscriptions, website benefits, organization locations, and Premium AI workflows.
                  </Text>
                </article>
              </div>
            </div>
          </div>
        </section>

        <div className="olreadia-mini-features is-bottom" aria-label="Olreadia trust signals">
          <div className="olreadia-mini-feature">
            <ThunderboltOutlined />
            <span>AI-Assisted</span>
          </div>
          <div className="olreadia-mini-feature blue">
            <SafetyCertificateOutlined />
            <span>Care Records</span>
          </div>
          <div className="olreadia-mini-feature gold">
            <CheckCircleOutlined />
            <span>Staff Operations</span>
          </div>
        </div>

        <section className="olreadia-bottom-card">
          <h2 className="olreadia-bottom-title">Start with OCarely today</h2>
          <div className="olreadia-action-row">
            <Button
              type="primary"
              icon={<LoginOutlined />}
              className="olreadia-pill-button"
              onClick={goToLogin}
              style={{ background: tokens.green, borderColor: tokens.green }}
            >
              Login
            </Button>
            <Button
              icon={<UserAddOutlined />}
              className="olreadia-pill-button"
              onClick={goToSignup}
              style={{
                color: tokens.green,
                background: tokens.greenSoft,
                borderColor: "#D6F0E1",
              }}
            >
              Get Started
            </Button>
            <Button
              icon={<ShareAltOutlined />}
              className="olreadia-pill-button"
              onClick={() => setShareOpen(true)}
              style={{
                color: tokens.blue,
                background: tokens.blueSoft,
                borderColor: "#D7EAFE",
              }}
            >
              Share
            </Button>
          </div>
        </section>

        <footer className="olreadia-footer">
          <span>Copyright © {currentYear} Oliveeb LLC. All rights reserved.</span>
          <span>OCarely helps care facilities and care workers manage care operations in one workspace.</span>
        </footer>
      </div>

      <Modal
        open={shareOpen}
        onCancel={() => setShareOpen(false)}
        footer={null}
        centered
        closeIcon={<CloseOutlined />}
      >
        <span className="olreadia-modal-badge">Share Olreadia</span>
        <h2 className="olreadia-modal-title">Invite others to OCarely</h2>
        <p className="olreadia-modal-desc">
          Help care facilities and care workers manage residents, assessments, schedules, daily notes, documents, websites, and AI-assisted care workflows.
        </p>
        <div className="olreadia-share-link">{APP_SHARE_LINK}</div>
        <div className="olreadia-modal-actions">
          <Button icon={<CopyOutlined />} onClick={copyShareLink}>
            Copy Link
          </Button>
          <Button type="primary" icon={<ShareAltOutlined />} onClick={nativeShare}>
            Share Link
          </Button>
        </div>
      </Modal>

      <JobDetailsDrawer
        open={jobDrawerOpen}
        loading={jobDrawerLoading}
        job={selectedJob}
        onApply={applyFromJobDrawer}
        onOpenDetails={() => openJobDetailsPage(selectedJob)}
        onShare={() => shareJobDetails(selectedJob)}
        showPosterDetails={false}
        onClose={closeJobDrawer}
      />

      <LandingTalentDetailsDrawer
        open={talentDrawerOpen}
        loading={talentDrawerLoading}
        profile={selectedTalent}
        onContact={contactFromTalentDrawer}
        onClose={closeTalentDrawer}
      />

      <SignInRequiredModal
        open={signInPromptOpen}
        onClose={closeSignInPrompt}
        title={publicContent ? "Login to continue" : isTalent ? "Login to contact talent" : "Login to apply"}
        message="Please log in or create an account to proceed."
      />

      <Modal
        open={premiumOpen}
        onCancel={() => setPremiumOpen(false)}
        footer={null}
        centered
        closeIcon={<CloseOutlined />}
      >
        <span
          className="olreadia-modal-badge"
          style={{
            color: tokens.darkRed,
            background: tokens.goldSoft,
            borderColor: tokens.goldBorder,
          }}
        >
          Premium Tools
        </span>
        <h2 className="olreadia-modal-title">Choose a premium option</h2>
        <p className="olreadia-modal-desc">
          Create an account to manage Premium AI tools, document extraction, summaries, care workflow drafts, and workspace upgrades.
        </p>
        <div className="olreadia-modal-actions">
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => {
              setPremiumOpen(false);
              goToSignup();
            }}
          >
            Create Account
          </Button>
          <Button
            icon={<EnvironmentOutlined />}
            onClick={() => {
              setPremiumOpen(false);
              openAccountPrompt();
            }}
          >
            Continue as Guest
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default PublicProductPage;
